import React, { useEffect, useState } from 'react';
import { Form, Col } from 'react-bootstrap';
import { ChevronDown, ChevronUp, ToggleOn, ToggleOff } from 'react-bootstrap-icons';
import CONSTANTS from '../../constants';

function WorkspaceAppAccessSection({
    workspaceData = [],
    onAccessChange,
    initialSelections = {},
    resetSignal = 0,
    title = 'Workspace and App access'
}) {
    const [expandedWorkspaces, setExpandedWorkspaces] = useState({});
    const [workspaceSelections, setWorkspaceSelections] = useState({});

    const normalizeApps = (apps) => {
        if (Array.isArray(apps)) {
            return apps;
        }
        if (typeof apps === 'string') {
            return apps
                .split(',')
                .map((app) => app.trim())
                .filter(Boolean);
        }
        return [];
    };

    const buildAccessPayload = (selections) => ({
        workspaceSelections: selections,
        workspacesForSubmit: Object.values(selections).map((selection) => ({
            workspaceId: selection.workspaceId,
            apps: normalizeApps(selection.apps)
        }))
    });

    const getWorkspaceIdByName = (workspaceName) => {
        return workspaceData.find((workspace) => workspace.tenantName === workspaceName)?.tenantID;
    };

    const toggleWorkspaceExpanded = (workspaceName) => {
        setExpandedWorkspaces((prev) => ({
            ...prev,
            [workspaceName]: !prev[workspaceName]
        }));
    };

    const toggleWorkspaceSelection = (workspaceName, workspaceId) => {
        setWorkspaceSelections((prev) => {
            const next = { ...prev };
            if (next[workspaceName]) {
                delete next[workspaceName];
            } else {
                next[workspaceName] = {
                    workspaceId,
                    apps: normalizeApps(next[workspaceName]?.apps)
                };
            }
            if (typeof onAccessChange === 'function') {
                onAccessChange(buildAccessPayload(next));
            }
            return next;
        });
    };

    const toggleAppSelection = (workspaceName, appType) => {
        setWorkspaceSelections((prev) => {
            const workspaceId = prev[workspaceName]?.workspaceId || getWorkspaceIdByName(workspaceName);
            if (!workspaceId) {
                return prev;
            }

            const existingApps = normalizeApps(prev[workspaceName]?.apps);
            const nextApps = existingApps.includes(appType)
                ? existingApps.filter((app) => app !== appType)
                : [...existingApps, appType];

            const next = {
                ...prev,
                [workspaceName]: {
                    workspaceId,
                    apps: normalizeApps(nextApps)
                }
            };

            if (typeof onAccessChange === 'function') {
                onAccessChange(buildAccessPayload(next));
            }

            return next;
        });
    };

    const isWorkspaceSelected = (workspaceName) => !!workspaceSelections[workspaceName];
    const isWorkspaceExpanded = (workspaceName) => !!expandedWorkspaces[workspaceName];
    const isAppSelected = (workspaceName, appType) => normalizeApps(workspaceSelections[workspaceName]?.apps).includes(appType);

    useEffect(() => {
        setExpandedWorkspaces({});
        setWorkspaceSelections(initialSelections);
    }, [resetSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <p className='h4'>{title}</p>

            <div className="workspace-access-container">
                {workspaceData.map((workspace) => (
                    <div key={workspace.tenantID} className="workspace-item p-03 border-primary-light border-radius-medium m-b-end-03">
                        <div
                            className="display-flex align-items-center cursor-pointer justify-between"
                            onClick={() => toggleWorkspaceExpanded(workspace.tenantName)}
                        >
                            <Col className='display-flex align-items-center'>
                                <Form.Check
                                    type="checkbox"
                                    id={`workspace-${workspace.tenantID}`}
                                    checked={isWorkspaceSelected(workspace.tenantName)}
                                    style={{ marginRight: '8px' }}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggleWorkspaceSelection(workspace.tenantName, workspace.tenantID)}
                                />
                                <span className="body-medium-condensed">{workspace.tenantName}</span>
                            </Col>
                            {isWorkspaceExpanded(workspace.tenantName)
                                ? <ChevronUp size={16} style={{ marginRight: '8px' }} />
                                : <ChevronDown size={16} style={{ marginRight: '8px' }} />
                            }
                        </div>

                        {isWorkspaceExpanded(workspace.tenantName) && (
                            <div className="apps-list" style={{ padding: '8px 16px', backgroundColor: '#ffffff' }}>
                                <div className="apps-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px' }}>
                                    {workspace.appList?.map((app) => (
                                        <div
                                            key={app.fields.apptype}
                                            className="app-item"
                                            style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}
                                        >
                                            {isAppSelected(workspace.tenantName, app.fields.apptype)
                                                ? <ToggleOn className='m-i-end-04 add-pointer svg-16' onClick={() => toggleAppSelection(workspace.tenantName, app.fields.apptype)} />
                                                : <ToggleOff className='m-i-end-04 add-pointer svg-16' onClick={() => toggleAppSelection(workspace.tenantName, app.fields.apptype)} />
                                            }
                                            <span className="body-medium-condensed">{CONSTANTS.APPNAMES_BY_PATH[app.fields.apptype] || app.fields.apptype}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </>
    );
}

export default WorkspaceAppAccessSection;